import type { IconType } from 'react-icons';
import { Link, useLocation } from 'react-router-dom';
import { useIsSelfRegistered } from '@/hooks/redux';
import { useInplayCounts } from '@/hooks/useInplayCounts';
import { useTranslation, type TranslationKey } from '@/i18n';
import { isGuestUser } from '@/utils';
import { Icons } from '@/utils/Icons';

type NavItem = {
  key: string;
  label: TranslationKey;
  icon: IconType;
  active: boolean;
  to: string;
  count?: number;
};

const itemClass = (active: boolean) =>
  `group relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors
   xl:flex-none xl:flex-row xl:gap-2 xl:rounded-full xl:px-5 xl:py-2.5 xl:text-sm
   ${active ? 'text-dock-fg xl:bg-gold xl:text-gold-foreground xl:shadow-md' : 'text-dock-muted hover:text-dock-fg xl:hover:bg-dock-hover'}`;

/**
 * Bottom navigation, fixed to the bottom of every page. Phones and tablets get a full-width
 * bar; desktop (where the sidebar is showing) gets a floating dock centred on the page column.
 * Guests see Home / In Play / Offers; logged-in users see Home / In Play /
 * My Markets / Referral / Passbook, with Profile in place of Referral for agent-created users.
 */
const BottomNav = () => {
  const { pathname, search } = useLocation();
  const { t } = useTranslation();
  const { total } = useInplayCounts();
  const isSelfRegistered = useIsSelfRegistered();
  const isGuest = isGuestUser();
  const isInplay = pathname === '/home' && new URLSearchParams(search).get('view') === 'inplay';

  const link = (key: string, label: TranslationKey, icon: IconType, to: string): NavItem => ({
    key,
    label,
    icon,
    to,
    active: pathname.startsWith(to),
  });

  const items: NavItem[] = [
    {
      key: 'home',
      label: 'bottomNav.home',
      icon: Icons.Home,
      to: '/home',
      active: pathname === '/home' && !isInplay,
    },
    {
      key: 'inplay',
      label: 'bottomNav.inPlay',
      icon: Icons.play,
      to: '/home?view=inplay',
      active: isInplay,
      count: total,
    },
    ...(isGuest
      ? [link('offers', 'bottomNav.offers', Icons.offers, '/offers')]
      : [
          link('myMarkets', 'bottomNav.myMarkets', Icons.Eye, '/user/bet-history'),
          isSelfRegistered
            ? link('referral', 'bottomNav.referral', Icons.referral, '/referral')
            : link('profile', 'bottomNav.profile', Icons.User, '/profile'),
          link('passbook', 'bottomNav.passbook', Icons.passbook, '/user/account-statement'),
        ]),
  ];

  return (
    <>
      {/* Keeps the end of the page (footer) clear of the fixed bar. */}
      <div aria-hidden="true" className="h-[calc(60px+env(safe-area-inset-bottom))] xl:h-24" />

      <nav
        aria-label="Bottom navigation"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-dock-border bg-dock pb-[env(safe-area-inset-bottom)] shadow-[0_-6px_20px_rgb(0_0_0/0.35)]
          xl:inset-x-auto xl:bottom-5 xl:left-[calc(50%+137px)] xl:-translate-x-1/2 xl:gap-1 xl:rounded-full xl:border xl:p-1.5 xl:shadow-[0_12px_32px_rgb(0_0_0/0.5)] xl:ring-4 xl:ring-shell/60 2xl:left-[calc(50%+147px)]"
      >
        {items.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            aria-current={item.active ? 'page' : undefined}
            className={itemClass(item.active)}
          >
            <span
              className={`relative flex h-7 w-12 items-center justify-center rounded-full transition-colors xl:size-auto
                  ${item.active ? 'bg-gold text-gold-foreground xl:bg-transparent' : ''}`}
            >
              <item.icon aria-hidden="true" className="size-[18px] xl:size-4" />
            </span>
            <span className={`max-w-full truncate ${item.active ? 'font-semibold' : ''}`}>
              {t(item.label)}
            </span>
            {/* Over the icon on phones, after the label on the desktop dock. */}
            {!!item.count && (
              <span className="absolute left-1/2 top-1 ml-2 min-w-[18px] rounded-full bg-danger-red px-1 text-center text-[10px] font-bold leading-4 text-body-inverse xl:static xl:ml-0">
                {item.count}
              </span>
            )}
          </Link>
        ))}
      </nav>
    </>
  );
};

export default BottomNav;
